import { useEffect, useMemo, useState } from 'react';
import { fetchBacklog } from '../api';
import { useAuth } from '../auth/AuthProvider';
import { useAppData } from '../AppData';
import { BacklogTask, STATUS_LABELS, VISIBLE_STATUSES, PROYECTO_STATUSES, PROYECTO_STATUS_LABELS } from '../domain/types';
import { normalizeAll } from '../domain/normalize';
import { nameByEmail, errMsg } from '../domain/util';
import { MultiSelect } from '../components/MultiSelect';
import BoardGeneral from './dashboards/BoardGeneral';
import BoardProgreso from './dashboards/BoardProgreso';
import BoardEspacio from './dashboards/BoardEspacio';
import BoardOperativa from './dashboards/BoardOperativa';
import BoardTipos from './dashboards/BoardTipos';
import BoardRetrabajo from './dashboards/BoardRetrabajo';
import BoardEquipo from './dashboards/BoardEquipo';
import BoardTiempos from './dashboards/BoardTiempos';
import BoardCalidad from './dashboards/BoardCalidad';
import BoardRevisiones from './dashboards/BoardRevisiones';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'progreso', label: 'Progreso por espacio' },
  { id: 'espacio', label: 'Por espacio' },
  { id: 'operativa', label: 'Operativa' },
  { id: 'tipos', label: 'Tipos / Urgencias' },
  { id: 'retrabajo', label: 'Retrabajo' },
  { id: 'revisiones', label: 'Revisiones' },
  { id: 'equipo', label: 'Equipo' },
  { id: 'tiempos', label: 'Tiempos / SLA' },
  { id: 'calidad', label: 'Calidad de datos' },
] as const;
type TabId = (typeof TABS)[number]['id'];

export default function Dashboards() {
  const { role, email } = useAuth();
  const { users, dimensions, reviewByTask, myEspacios } = useAppData();
  const [tasks, setTasks] = useState<BacklogTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  // El responsable de espacio solo ve la hoja "Progreso por espacio".
  const isSpaceManager = role === 'space_manager';
  const visibleTabs = isSpaceManager ? TABS.filter((t) => t.id === 'progreso') : TABS;
  const [tab, setTab] = useState<TabId>(isSpaceManager ? 'progreso' : 'general');

  // Filtros globales
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [espacios, setEspacios] = useState<string[]>([]);
  const [sede, setSede] = useState('');
  const [responsable, setResponsable] = useState('');
  const [urgencia, setUrgencia] = useState('');
  const [estado, setEstado] = useState('');
  // Filtros multi-selección
  const [proyectos, setProyectos] = useState<string[]>([]);
  const [direcciones, setDirecciones] = useState<string[]>([]);
  const [categorias, setCategorias] = useState<string[]>([]);
  const [estadoProyecto, setEstadoProyecto] = useState<string[]>([]);

  // Mapa proyecto -> estado del proyecto, para filtrar por estado del proyecto.
  const proyectoStatus = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of dimensions.proyecto || []) m.set(d.name, d.status || '');
    return m;
  }, [dimensions.proyecto]);

  useEffect(() => {
    (async () => {
      try { setTasks(await fetchBacklog(role!, email)); }
      catch (e) { setErr(errMsg(e)); }
      setLoading(false);
    })();
  }, [role, email]);

  const normalized = useMemo(() => normalizeAll(tasks, undefined, reviewByTask), [tasks, reviewByTask]);

  const filtered = useMemo(() => normalized.filter((t) => {
    const ref = (t.fecha_solicitud || '').slice(0, 10);
    if (from && ref < from) return false;
    if (to && ref > to) return false;
    if (espacios.length && !espacios.includes(t.solicitante)) return false;
    if (sede && t.sede !== sede) return false;
    if (responsable && t.responsable !== responsable) return false;
    if (urgencia && t.urgencia !== urgencia) return false;
    if (estado && t.estado !== estado) return false;
    if (proyectos.length && !proyectos.includes(t.proyecto)) return false;
    if (direcciones.length && !direcciones.includes(t.direccion)) return false;
    if (categorias.length && !categorias.includes(t.area_produccion)) return false;
    if (estadoProyecto.length && !estadoProyecto.includes(proyectoStatus.get(t.proyecto) || '')) return false;
    return true;
  }), [normalized, from, to, espacios, sede, responsable, urgencia, estado, proyectos, direcciones, categorias, estadoProyecto, proyectoStatus]);

  if (loading) return <p className="muted">Cargando métricas…</p>;
  if (err) return <div className="error">{err}</div>;

  const activeCount =
    [from, to, sede, responsable, urgencia, estado].filter(Boolean).length +
    [espacios, proyectos, direcciones, categorias, estadoProyecto].filter((a) => a.length).length;
  const filtersOn = activeCount > 0;
  const clearFilters = () => {
    setFrom(''); setTo(''); setSede(''); setResponsable(''); setUrgencia(''); setEstado('');
    setEspacios([]);
    setProyectos([]); setDirecciones([]); setCategorias([]); setEstadoProyecto([]);
  };
  // El responsable ve todos los proyectos/tareas; solo el KPI de revisiones
  // queda acotado a sus espacios.
  const props = { tasks: filtered, users, proyectoStatus, reviewEspacios: isSpaceManager ? myEspacios : undefined };

  // Solicitantes (espacio + cliente) para el filtro unificado.
  const solicitantes = [
    ...(dimensions.espacio || []).map((d) => d.name),
    ...(dimensions.cliente_interno || []).map((d) => d.name),
  ];

  const opt = (names: string[]) => names.map((n) => ({ value: n, label: n }));
  const espacioOpts = opt(solicitantes);
  const proyectoOpts = opt((dimensions.proyecto || []).map((d) => d.name));
  const direccionOpts = opt((dimensions.direccion || []).map((d) => d.name));
  const categoriaOpts = opt((dimensions.categoria || []).map((d) => d.name));
  const estadoProyectoOpts = PROYECTO_STATUSES.map((s) => ({ value: s, label: PROYECTO_STATUS_LABELS[s] }));

  return (
    <div className="grid">
      <div className="dashboard-hero">
        <div className="dashboard-hero-copy">
          <span className="eyebrow">Universidad del Gran Rosario</span>
          <h1>Tablero de seguimiento</h1>
          <p>
            Vista ejecutiva del backlog de Comunicación y Marketing, con foco en seguimiento
            operativo, análisis de la calidad de entrega y calidad de datos.
          </p>
        </div>
        <div className="dashboard-hero-brand" aria-label="Identidad Universidad del Gran Rosario">
          <img src="/brand/ugr-logo-horizontal.svg" alt="Universidad del Gran Rosario" />
          <div>
            <strong>Backlog UGR</strong>
            <span>Comunicación y Marketing</span>
          </div>
        </div>
      </div>

      {isSpaceManager && (
        <div className="panel" style={{ borderLeft: '3px solid var(--brand, #005EB8)' }}>
          {myEspacios.length > 0 ? (
            <p style={{ margin: 0 }}>
              Ves todos los proyectos y tareas. Las revisiones que gestionás y el KPI de revisiones
              corresponden a tus espacios: <strong>{myEspacios.join(', ')}</strong>.
            </p>
          ) : (
            <p className="muted" style={{ margin: 0 }}>
              No tenés espacios asignados. Pedile a un administrador que te asigne al menos uno para
              poder gestionar revisiones.
            </p>
          )}
        </div>
      )}

      {/* Filtros globales */}
      <div className="panel dashboard-filters filter-bar">
        <div className="filter-bar-head">
          <div className="filter-bar-title">
            <span className="eyebrow">Filtros</span>
            {activeCount > 0 && <span className="filter-badge">{activeCount}</span>}
          </div>
          <div className="filter-bar-actions">
            <span className="muted filter-count">{filtered.length} de {tasks.length} pedidos</span>
            {filtersOn && <button className="ghost sm" onClick={clearFilters}>Limpiar filtros</button>}
          </div>
        </div>

        <div className="filter-row">
          <span className="filter-row-caption">Período</span>
          <div className="filter-row-fields">
            <div className="field"><label>Desde</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
            <div className="field"><label>Hasta</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
          </div>
        </div>

        <div className="filter-row">
          <span className="filter-row-caption">Proyecto</span>
          <div className="filter-row-fields">
            <MultiSelect label="Proyecto" options={proyectoOpts} selected={proyectos} onChange={setProyectos} allLabel="Todos" />
            <MultiSelect label="Dirección" options={direccionOpts} selected={direcciones} onChange={setDirecciones} allLabel="Todas" />
            <MultiSelect label="Estado de proyecto" options={estadoProyectoOpts} selected={estadoProyecto} onChange={setEstadoProyecto} allLabel="Todos" />
          </div>
        </div>

        <div className="filter-row">
          <span className="filter-row-caption">Solicitud</span>
          <div className="filter-row-fields">
            <MultiSelect label="Espacio / cliente" options={espacioOpts} selected={espacios} onChange={setEspacios} allLabel="Todos" />
            <div className="field">
              <label>Sede</label>
              <select value={sede} onChange={(e) => setSede(e.target.value)}>
                <option value="">Todas</option>
                {(dimensions.sede || []).map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </div>
            <MultiSelect label="Categoría" options={categoriaOpts} selected={categorias} onChange={setCategorias} allLabel="Todas" />
          </div>
        </div>

        <div className="filter-row">
          <span className="filter-row-caption">Tarea</span>
          <div className="filter-row-fields">
            <div className="field">
              <label>Responsable</label>
              <select value={responsable} onChange={(e) => setResponsable(e.target.value)}>
                <option value="">Todos</option>
                {users.map((u) => <option key={u.email} value={u.email}>{nameByEmail(u.email, users)}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Urgencia</label>
              <select value={urgencia} onChange={(e) => setUrgencia(e.target.value)}>
                <option value="">Todas</option><option value="baja">baja</option><option value="media">media</option><option value="alta">alta</option>
              </select>
            </div>
            <div className="field">
              <label>Estado</label>
              <select value={estado} onChange={(e) => setEstado(e.target.value)}>
                <option value="">Todos</option>
                {VISIBLE_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Pestañas */}
      <div className="row dashboard-tabs" style={{ gap: 4, flexWrap: 'wrap', borderBottom: '1px solid var(--border)', paddingBottom: 4 }}>
        {visibleTabs.map((t) => (
          <span key={t.id} className={`chip ${tab === t.id ? 'active' : ''}`} style={{ cursor: 'pointer' }} onClick={() => setTab(t.id)}>{t.label}</span>
        ))}
      </div>

      {tab === 'general' && <BoardGeneral {...props} />}
      {tab === 'progreso' && <BoardProgreso {...props} />}
      {tab === 'espacio' && <BoardEspacio {...props} />}
      {tab === 'operativa' && <BoardOperativa {...props} />}
      {tab === 'tipos' && <BoardTipos {...props} />}
      {tab === 'retrabajo' && <BoardRetrabajo {...props} />}
      {tab === 'revisiones' && <BoardRevisiones {...props} />}
      {tab === 'equipo' && <BoardEquipo {...props} />}
      {tab === 'tiempos' && <BoardTiempos {...props} />}
      {tab === 'calidad' && <BoardCalidad {...props} dimensions={dimensions} />}
    </div>
  );
}
